/**
 * Paliers de développement du mode `?demo` : en mémoire, mutés par les mêmes
 * règles que l'API (révision, motif obligatoire, cœur non verrouillable).
 *
 * État de départ cohérent : le MVP (le cœur) est inclus, la V1 est débloquée,
 * la V2 l'est en partie (la foire, le hors-ligne et les mouvements), la
 * version finale est verrouillée. Le coffre, débloqué par la V1, est par
 * ailleurs coupé en urgence (voir `demoData.ts`) : les deux axes sont
 * indépendants.
 */
import {
  estModule,
  estModuleCoeur,
  MODULES_COEUR,
  MODULES_OPTIONNELS,
  modulesDeLaGalerie,
  REGISTRE_MODULES,
} from '@artosera/shared';
import { ApiError } from './api.js';
import type { IdPalierDev, ModulePalierVue, PalierDevVue, PaliersVue } from '../types.js';

/** Les modules de chaque palier, dans l'ordre d'affichage (hors MVP : le cœur). */
const MODULES_DU_PALIER: Record<Exclude<IdPalierDev, 'mvp'>, readonly string[]> = {
  v1: [
    'facturation',
    'documents-prestige',
    'editions-provenance',
    'coffre',
    'contrats-artistes',
    'modeles-contrats',
    'roles-fins',
  ],
  v2: ['mode-foire', 'application-hors-ligne', 'mouvements', 'viewing-rooms', 'offres-relances'],
  finale: [
    'site',
    'boutique',
    'newsletter-reseaux',
    'agenda-presse',
    'artsy-artnet',
    'api-comptabilite',
    'sso',
    'console-groupe',
  ],
};

const IDS_VERROUILLABLES = Object.keys(MODULES_DU_PALIER) as Array<Exclude<IdPalierDev, 'mvp'>>;

interface Trace {
  modifieLe: string;
  modifiePar: string;
  motif: string;
}

const debloques = new Set<string>([
  ...MODULES_DU_PALIER.v1,
  'mode-foire',
  'application-hors-ligne',
  'mouvements',
]);
const traces = new Map<string, Trace>(
  [...debloques].map((module) => [
    module,
    {
      modifieLe: '2026-09-20T09:00:00.000Z',
      modifiePar: 'demo@artosera.com',
      motif: 'Palier débloqué pour toute la plateforme.',
    },
  ]),
);
let revision = 4;

/** Ce que la démo sait de l'urgence : coupures de la console et variable de secours. */
export interface UrgenceDemo {
  coupes: ReadonlySet<string>;
  secours: ReadonlySet<string>;
}

export function palierDuModuleDemo(module: string): IdPalierDev | null {
  for (const id of IDS_VERROUILLABLES) {
    if (MODULES_DU_PALIER[id].includes(module)) return id;
  }
  return null;
}

function moduleVue(module: string, urgence: UrgenceDemo, coeur: boolean): ModulePalierVue {
  const definition = estModule(module) ? REGISTRE_MODULES[module] : undefined;
  if (definition?.famille === 'addon') {
    throw new ApiError(400, 'validation.invalide', 'Les add-ons sont réglés par leur publication.');
  }
  const trace = traces.get(module);
  return {
    id: module,
    famille: definition?.famille ?? 'complementaire',
    categorie: definition && 'categorie' in definition ? (definition.categorie ?? null) : null,
    debloque: coeur || debloques.has(module),
    coupeUrgence: urgence.coupes.has(module),
    coupeSecours: urgence.secours.has(module),
    livre: definition?.disponible ?? false,
    disponible: modulesDeLaGalerie(
      { modulesActifs: MODULES_OPTIONNELS },
      new Set([
        ...urgence.coupes,
        ...urgence.secours,
        ...MODULES_OPTIONNELS.filter((id) => !debloques.has(id)),
      ]),
    ).includes(module as (typeof MODULES_OPTIONNELS)[number]),
    modifieLe: trace?.modifieLe ?? null,
    modifiePar: trace?.modifiePar ?? null,
    motif: trace?.motif ?? null,
  };
}

export function paliersDemo(urgence: UrgenceDemo): PaliersVue {
  const mvp: PalierDevVue = {
    id: 'mvp',
    verrouillable: false,
    debloques: MODULES_COEUR.length,
    total: MODULES_COEUR.length,
    modules: MODULES_COEUR.map((module) => moduleVue(module, urgence, true)),
  };
  const autres = IDS_VERROUILLABLES.map((id): PalierDevVue => {
    const modules = MODULES_DU_PALIER[id].map((module) => moduleVue(module, urgence, false));
    return {
      id,
      verrouillable: true,
      debloques: modules.filter((module) => module.debloque).length,
      total: modules.length,
      modules,
    };
  });
  return { paliers: [mvp, ...autres], revision };
}

/** Pour `GET /interrupteurs` : le palier de chaque module optionnel et son état. */
export function modulesParPalierDemo(): Array<{
  module: string;
  palier: IdPalierDev;
  debloque: boolean;
}> {
  return MODULES_OPTIONNELS.flatMap((module) => {
    const palier = palierDuModuleDemo(module);
    return palier ? [{ module, palier, debloque: debloques.has(module) }] : [];
  });
}

function controler(motif: string, revisionLue: number): string {
  const texte = motif.trim();
  if (texte.length < 3) throw new ApiError(400, 'validation.invalide', 'Indiquez le motif.');
  if (revisionLue !== revision) {
    throw new ApiError(409, 'conflit.version', 'Les paliers ont changé entre-temps.', {
      raison: 'revision_paliers_perimee',
    });
  }
  return texte;
}

function appliquer(modules: readonly string[], debloque: boolean, texte: string): void {
  const trace: Trace = {
    modifieLe: new Date().toISOString(),
    modifiePar: 'demo@artosera.com',
    motif: texte,
  };
  for (const module of modules) {
    if (debloque) debloques.add(module);
    else debloques.delete(module);
    traces.set(module, trace);
  }
  revision += 1;
}

export function basculerPalierDemo(
  palier: string,
  debloque: boolean,
  motif: string,
  revisionLue: number,
  urgence: UrgenceDemo,
): PaliersVue {
  if (palier === 'mvp') {
    throw new ApiError(400, 'validation.invalide', 'Le MVP est inclus : il ne se verrouille pas.', {
      raison: 'palier_non_verrouillable',
    });
  }
  if (!(palier in MODULES_DU_PALIER)) {
    throw new ApiError(404, 'ressource.introuvable', 'Palier inconnu.');
  }
  const texte = controler(motif, revisionLue);
  appliquer(MODULES_DU_PALIER[palier as keyof typeof MODULES_DU_PALIER], debloque, texte);
  return paliersDemo(urgence);
}

export function basculerModulePalierDemo(
  module: string,
  debloque: boolean,
  motif: string,
  revisionLue: number,
  urgence: UrgenceDemo,
): PaliersVue {
  if (!estModule(module)) throw new ApiError(404, 'ressource.introuvable', 'Module inconnu.');
  if (estModuleCoeur(module)) {
    throw new ApiError(
      400,
      'validation.invalide',
      'Le cœur est inclus : il ne se verrouille pas.',
      {
        raison: 'module_non_verrouillable',
      },
    );
  }
  const texte = controler(motif, revisionLue);
  appliquer([module], debloque, texte);
  return paliersDemo(urgence);
}
